import PropTypes from 'prop-types';
import { forwardRef } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Card, CardContent, CardHeader, Divider, Typography, Box, CardActions } from '@mui/material';
import { styled } from '@mui/material/styles';

// project import
import Highlighter from '../third-party/Highlighter';

// header style
const headerSX = {
  p: '10px 20px 0px',
  '& .MuiCardHeader-action': { m: '0px auto', alignSelf: 'center' }
};

const subHeaderSX = {
  p: '8px 20px 0px',
  '& .MuiCardHeader-action': { m: '0px auto', alignSelf: 'center' }
};

// ==============================|| CUSTOM - MAIN CARD ||============================== //

const MainCard = forwardRef(
  (
    {
      border = true,
      boxShadow,
      children,
      content = true,
      contentSX = { display: 'flex', flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', flexDirection: 'column' },
      darkTitle,
      elevation,
      secondary,
      shadow,
      sx = {},
      title,
      subtitle,
      headerDivider,
      codeHighlight,
      fullHeight = true,
      padding = true,
      footer = false,
      footerAction,
      ...others
    },
    ref
  ) => {
    const theme = useTheme();
    boxShadow = theme.palette.mode === 'dark' ? boxShadow || true : boxShadow;

    return (
      <Card
        elevation={elevation || 0}
        ref={ref}
        id="main-card"
        {...others}
        sx={{
          border: border ? '1px solid' : 'none',
          borderRadius: 2,
          borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey.A800,
          boxShadow: boxShadow && (!border || theme.palette.mode === 'dark') ? shadow || theme.customShadows.z1 : 'inherit',
          // maxHeight: 'calc(100vh - 115px)',
          // height: fullHeight ? '100%' : 'auto',
          maxHeight: '100%',
          display: 'flex',
          overflowY: 'auto',
          flexDirection: 'column',
          width: '100%',
          ':hover': {
            boxShadow: boxShadow ? shadow || theme.customShadows.z1 : 'inherit'
          },
          '& pre': {
            m: 0,
            p: '16px !important',
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.75rem'
          },
          ...sx
        }}
      >
        {/* card header and action */}
        <Box sx={{ flexShrink: 0 }}>
          {!darkTitle && title && <CardHeader sx={headerSX} titleTypographyProps={{ variant: 'h5' }} title={title} action={secondary} />}
          {darkTitle && title && <CardHeader sx={headerSX} title={<Typography variant="h3">{title}</Typography>} action={secondary} />}
          {subtitle && (
            <Typography variant="subtitle1" className="text-grey-600" sx={subHeaderSX}>
              {subtitle}
            </Typography>
          )}
          {headerDivider && <Divider sx={{ borderStyle: 'solid', marginTop: '10px' }} />}
        </Box>

        {/* card content */}
        {content && (
          <CardContent id="main-card-content" sx={{ ...contentSX, padding: padding ? '20px' : '0px' }}>
            {children}
          </CardContent>
        )}
        {!content && children}

        {/* card footer - clipboard & highlighter  */}
        {codeHighlight && (
          <>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Highlighter codeHighlight={codeHighlight} main>
              {children}
            </Highlighter>
          </>
        )}

        {/* card Footer & action */}
        {footer && (
          <Box sx={{ flexShrink: 0 }}>
            <Divider sx={{ borderStyle: 'solid' }} />
            <CardActions disableSpacing>{footerAction}</CardActions>
          </Box>
        )}
      </Card>
    );
  }
);

MainCard.propTypes = {
  border: PropTypes.bool,
  boxShadow: PropTypes.bool,
  contentSX: PropTypes.object,
  darkTitle: PropTypes.bool,
  divider: PropTypes.bool,
  elevation: PropTypes.number,
  secondary: PropTypes.node,
  shadow: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  codeHighlight: PropTypes.bool,
  content: PropTypes.bool,
  children: PropTypes.node,
  fullHeight: PropTypes.bool,
  padding: PropTypes.bool,
  headerDivider: PropTypes.bool,
  subtitle: PropTypes.string,
  footer: PropTypes.bool,
  footerAction: PropTypes.node
};

export default MainCard;
